import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OrderTypeEnums } from 'src/app/enum/order-enums';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConsolidatedReservationTableModel } from 'src/app/models/tables/preliminaries/consolidated-reservation-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConsolidatedReservationService } from 'src/app/services/preliminaries/consolidated-reservation.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-consolidated-reservation',
  templateUrl: './consolidated-reservation.component.html',
  styleUrls: ['./consolidated-reservation.component.scss'],
  standalone: false
})
export class ConsolidatedReservationComponent implements OnInit {

  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<ConsolidatedReservationTableModel> = new PagedResponseModel<ConsolidatedReservationTableModel>();
  generalHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  isOpened: string | undefined = undefined;
  limit: number = 10;
  listOrderType: ColumnResponseModel = new ColumnResponseModel();
  nameWindow: string = 'Reservas Consolidado';
  orderTypeId: number = OrderTypeEnums.rental;
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = 'consolidated-reservation';

  constructor(private headerService: HeaderService,
    private consolidatedReservationService: ConsolidatedReservationService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getPermission(),
      this.getColumnsForGeneralConsultation(),
      this.getConsolidatedReservation(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.consolidatedReservationService.exportGeneralConsultation(this.filters,
        this.columnSort, this.ascendant, this.orderTypeId
      ));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Reservas Consolidado.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar reservas consolidadas.', error);
    }
  }

  async getConsolidatedReservation() {
    try {
      const response = await firstValueFrom(this.consolidatedReservationService.getConsolidatedReservation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.orderTypeId
      ));
      this.generalData = response;
    } catch (error) {
      console.error('Error al buscar las reservas consolidadas.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.columns.length === 0) {
        const response = await firstValueFrom(this.consolidatedReservationService.getColumnsForGeneralConsultation());
        this.listOrderType = response.filtersGlobal[0];
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getConsolidatedReservation();
  }

  onOrderTypeChange(value: number) {
    this.orderTypeId = value;
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
